import React, { useState } from "react";
import { IDoctor } from "../../../../dto/interfaces";
import { Button } from "../../../../components/common/button";
import { Menu, X } from "lucide-react"; // Import icons for the hamburger menu
import { assets } from "../../../../assets/assets";

const navLinks = [
    { name: "Home", href: "#hero" },
    { name: "Services", href: "#services" },
    { name: "About", href: "#about-doctor" },
    { name: "Contact", href: "#contact-doctor" }
];

interface DoctorProfileParams {
    doctor: IDoctor;
}

const DoctorNavbar: React.FC<DoctorProfileParams> = ({ doctor }) => {
    // State to manage the mobile menu's open/closed status
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Function to close the menu, useful for link clicks
    const closeMenu = () => setIsMenuOpen(false);

    const onBookAppointment = () => {
        // Find the booking section element by its ID
        const bookingSection = document.getElementById("book-appointment");

        if (bookingSection) {
            // Use the browser's built-in smooth scroll
            bookingSection.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Fallback if the element isn't found
            console.warn("Booking section with id 'book-appointment' not found.");
        }
    };

    return (
        <header className="w-full bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
            <nav className="container mx-auto flex items-center justify-between px-6 py-4">
                {/* === Logo Section (Unchanged) === */}
                <a href="#" className="flex items-center gap-3">
                    <img
                        src={doctor?.clinicImage || assets.placeholder_clinic_logo}
                        alt={doctor?.clinicName || "Clinic Logo"}
                        className="h-10 w-10 object-contain"
                    />
                    <div>
                        <span className="block text-xl font-bold text-gray-900">{doctor?.clinicName}</span>
                        <span className="block text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Health Care
                        </span>
                    </div>
                </a>

                {/* === Desktop Navigation Links and Button === */}
                {/* This is hidden on screens smaller than 'md' */}
                <div className="hidden md:flex items-center gap-8">
                    {/* Middle Links */}
                    <div className="flex items-center gap-6">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                className="text-base text-gray-700 font-medium hover:text-blue-600 transition-colors duration-200"
                            >
                                {link.name}
                            </a>
                        ))}
                    </div>

                    {/* "Book Appointment" Button */}
                    <Button onClick={onBookAppointment}>Book Appointment</Button>
                </div>

                {/* === Mobile Hamburger Button === */}
                {/* This is only visible on screens smaller than 'md' */}
                <div className="md:hidden">
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)} // Toggle menu state
                        aria-label="Toggle navigation menu"
                    >
                        {isMenuOpen ? (
                            <X className="w-6 h-6 text-gray-800" />
                        ) : (
                            <Menu className="w-6 h-6 text-gray-800" />
                        )}
                    </button>
                </div>
            </nav>

            {/* === Mobile Menu === */}
            {/* This dropdown menu is shown/hidden based on 'isMenuOpen' state */}
            {/* It's only rendered on screens smaller than 'md' thanks to 'md:hidden' */}
            <div
                className={`md:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-200 ${
                    isMenuOpen ? "block" : "hidden"
                }`}
            >
                <div className="flex flex-col px-6 py-4 space-y-3">
                    {/* Mobile Navigation Links */}
                    {navLinks.map((link) => (
                        <a
                            key={link.name}
                            href={link.href}
                            onClick={closeMenu} // Close menu on link click
                            className="block text-base text-gray-700 font-medium rounded-md py-2 hover:text-blue-600 hover:bg-gray-50"
                        >
                            {link.name}
                        </a>
                    ))}

                    {/* Mobile "Book Appointment" Button */}
                    <Button
                        onClick={() => {
                            onBookAppointment();
                            closeMenu(); // Close menu on button click
                        }}
                        className="w-full text-center" // Make button full width
                    >
                        Book Appointment
                    </Button>
                </div>
            </div>
        </header>
    );
};

export default DoctorNavbar;
